import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/rootReducer';
import { Spin } from 'antd';
import { commaSeperator, formatNumber } from '../../../../../utils/validation';
import {
    Bar,
    BarChart,
    Cell,
    Label,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { Cashflowgraph } from '../../../redux/types';
import moment from 'moment';

type Props = {
    dealName?: string;
    currency?: string;
};

const formatter = (value: string) => `${moment(value).format('DD MMM YY')}`;
const formatter1 = (value: string | number) => `${formatNumber(parseInt(String(value)))}`;

const CashflowSimulatorGraph: React.FC<Props> = ({ dealName, currency }) => {
    const loading = useSelector((state: RootState) => state.deal.cLoading);
    const list = useSelector((state: RootState) => state.deal.cashflowGraph);

    const pieData = useMemo(() => {
        let data: { name: 'Interest Payment' | 'Principal Repayment'; value: number }[] = [];
        let interestPayment = 0;
        let principalPayment = 0;
        list.forEach((i) => {
            interestPayment = interestPayment + i.interestPayment;
            principalPayment = principalPayment + i.principalPayment;
        });
        data = [
            {
                name: 'Interest Payment',
                value: interestPayment,
            },
            {
                name: 'Principal Repayment',
                value: principalPayment,
            },
        ];
        return data;
    }, [list]);

    return (
        <div className="w-full md:w-[65%] lg:w-[70%]">
            <div className="p-6 pt-20 md:pt-8  md:pl-14 h-full">
                <p className="text-[#20222B] hidden md:flex  font-[WorkSans-Semibold] font-semibold text-xl border-b pb-2 border-[#D7D7D7]">
                    See how {dealName} can work for you
                </p>
                <div className="h-full">
                    {loading && (
                        <div className="flex justify-center items-center h-full">
                            <Spin size="large" />
                        </div>
                    )}
                    {!loading && list.length && (
                        <div>
                            <CashflowSimulatorGrapth data={list} currency={currency} />
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 border border-[#D7D7D7]">
                                <div>
                                    <div className="border-r border-b border-[#D7D7D7] text-center py-4 px-4">
                                        <p className="text-[#5C5C5C] font-[WorkSans] text-sm">Total Interest Payment</p>
                                        <p className="text-[#20222B] font-[WorkSans-Semibold] font-bold text-sm">
                                            {commaSeperator(
                                                parseFloat(
                                                    String(
                                                        pieData.find((i) => i.name === 'Interest Payment')?.value || 0,
                                                    ),
                                                ).toFixed(2),
                                            )}
                                        </p>
                                    </div>
                                    <div className="border-r  border-[#D7D7D7] text-center py-4 px-4">
                                        <p className="text-[#5C5C5C] font-[WorkSans] text-sm">
                                            Total Payment (Principal + Interest)
                                        </p>
                                        <p className="text-[#20222B] font-[WorkSans-Semibold] font-bold text-sm">
                                            {commaSeperator(
                                                parseFloat(
                                                    String(
                                                        (pieData.find((i) => i.name === 'Interest Payment')?.value ||
                                                            0) +
                                                            (pieData.find((i) => i.name === 'Principal Repayment')
                                                                ?.value || 0),
                                                    ),
                                                ).toFixed(2),
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <ResponsiveContainer width="100%" height={140}>
                                        <PieChart width={100} height={80}>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={40}
                                                fill="#8884d8"
                                                dataKey="value"
                                                minAngle={10}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={entry.name === 'Interest Payment' ? '#142C53' : '#41D17E'}
                                                    />
                                                ))}
                                            </Pie>
                                            <Legend align="right" layout="vertical" verticalAlign="middle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <p className="text-[#5C5C5C] text-[8px] mt-4">
                                Disclaimer: The information presented in the chart above is for illustrative purposes
                                only and should not be construed as a guarantee of future performance or returns. The
                                data and projections are based on various assumptions, which may not reflect actual
                                future outcomes. Past performance is not indicative of future results, and any forecasts
                                or hypothetical examples provided do not represent actual investment returns. Helicap
                                Securities Pte. Ltd. does not make any warranty, express or implied, regarding the
                                accuracy, completeness, or reliability of the information provided. All investments
                                involve risk, including the potential loss of principal, and readers are advised to
                                conduct their own research and consult with a licensed financial advisor before making
                                any investment decisions.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CashflowSimulatorGraph;

type PropsC = {
    data: Cashflowgraph[];
    currency?: string;
};

const CashflowSimulatorGrapth: React.FC<PropsC> = ({ data, currency }) => {
    return (
        <div>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    width={500}
                    height={200}
                    data={data}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                    maxBarSize={10}
                >
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatter}
                        stroke="#EDEDED"
                        tick={{ fill: '#5C5C5C', fontSize: '0.8rem' }}
                    />
                    <YAxis
                        type="number"
                        stroke="#EDEDED"
                        tickFormatter={formatter1}
                        allowDataOverflow={false}
                        tick={{ fill: '#5C5C5C', fontFamily: 'WorkSans-Semibold', fontSize: '0.8rem' }}
                    >
                        <Label value={`Repayment (${currency})`} angle={-90} position={'insideLeft'} offset={-10} />
                    </YAxis>

                    <Bar dataKey="principalPayment" stackId="a" fill="#41D17E" />
                    <Bar dataKey="interestPayment" stackId="a" fill="#142C53" minPointSize={5} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active) {
        return (
            <div className="shadow rounded bg-white px-4 py-2">
                <p className="label font-semibold text-xs">{label}</p>
                {payload && payload[0] && (
                    <div className="desc">
                        Principal Repayment:
                        {commaSeperator(String(payload[0].value || 0))}
                    </div>
                )}
                {payload && payload[1] && (
                    <div className="desc">
                        Interest Repayment:
                        {commaSeperator(String(payload[1].value || 0))}
                    </div>
                )}
            </div>
        );
    }
    return null;
};
